import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeft, Mail, RefreshCw, LogOut, Trash2, CheckCheck, Archive, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

type ContactStatus = 'new' | 'contacted' | 'archived';

interface ContactMessage {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  serviceInterest?: string;
  message: string;
  status?: ContactStatus;
  createdAt: string;
}

const statusStyles: Record<ContactStatus, string> = {
  new:       'bg-amber-100 text-amber-700',
  contacted: 'bg-teal-100 text-teal-700',
  archived:  'bg-gray-100 text-gray-500',
};

const AdminContacts: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ContactStatus | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const token = useMemo(() => localStorage.getItem('admin_token'), []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out');
    navigate('/admin/login');
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      if (!token) { toast.error('Not logged in.'); return; }
      const res = await axios.get<{ items: ContactMessage[] } | ContactMessage[]>(
        `${API_BASE_URL}/api/admin/contacts?limit=200&skip=0`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      const list = Array.isArray(res.data) ? res.data : (res.data as any)?.items || [];
      setContacts(list);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch contacts.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContacts(); }, []); // eslint-disable-line

  const updateStatus = async (id: string, status: ContactStatus) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/admin/contacts/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContacts(prev => prev.map(c => c._id === id ? { ...c, status } : c));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error('Failed to update status.');
    }
  };

  const deleteContact = async (id: string) => {
    if (!window.confirm('Delete this message permanently?')) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/admin/contacts/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContacts(prev => prev.filter(c => c._id !== id));
      toast.success('Message deleted.');
    } catch {
      toast.error('Failed to delete message.');
    }
  };

  const filtered = filter === 'all' ? contacts : contacts.filter(c => (c.status || 'new') === filter);

  const counts = {
    all:       contacts.length,
    new:       contacts.filter(c => (c.status || 'new') === 'new').length,
    contacted: contacts.filter(c => c.status === 'contacted').length,
    archived:  contacts.filter(c => c.status === 'archived').length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#001a4d] text-white px-6 py-4 shadow sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Link
            to="/admin/dashboard"
            className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#0B8A8F] flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-extrabold text-base leading-tight">Contact Messages</div>
              <div className="text-white/50 text-xs">{counts.all} total · {counts.new} new</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={fetchContacts}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold transition text-sm"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-4">

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap">
          {(['all', 'new', 'contacted', 'archived'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition capitalize ${
                filter === f
                  ? 'bg-[#001a4d] text-white'
                  : 'bg-white border border-gray-200 text-gray-500 hover:border-[#001a4d] hover:text-[#001a4d]'
              }`}
            >
              {f} <span className="ml-1 opacity-60">({counts[f]})</span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-gray-500 text-sm py-10 text-center">Loading messages…</div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-400 text-sm py-10 text-center bg-white rounded-2xl border border-gray-100">
            No messages in this category.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(c => {
              const status: ContactStatus = (c.status || 'new') as ContactStatus;
              const isExpanded = expandedId === c._id;
              return (
                <div
                  key={c._id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  {/* Row */}
                  <div
                    className="flex items-start gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition"
                    onClick={() => setExpandedId(isExpanded ? null : c._id)}
                  >
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full bg-[#001a4d] flex items-center justify-center flex-shrink-0 text-white text-sm font-extrabold">
                      {(c.firstName?.[0] || '?').toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-[#001a4d] text-sm">
                          {c.firstName} {c.lastName}
                        </span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusStyles[status]}`}>
                          {status}
                        </span>
                        {c.company && (
                          <span className="text-xs text-gray-400">{c.company}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Mail size={11} className="text-gray-400" />
                        <a
                          href={`mailto:${c.email}`}
                          onClick={e => e.stopPropagation()}
                          className="text-xs text-[#0B8A8F] hover:underline"
                        >
                          {c.email}
                        </a>
                        {c.serviceInterest && (
                          <span className="text-xs text-gray-400 ml-2">· {c.serviceInterest}</span>
                        )}
                      </div>
                      {!isExpanded && (
                        <p className="text-xs text-gray-500 mt-1 truncate">{c.message}</p>
                      )}
                    </div>

                    <div className="flex-shrink-0 text-right">
                      <div className="text-xs text-gray-400">
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </div>
                      <div className="text-xs text-gray-300 mt-0.5">
                        {c.createdAt ? new Date(c.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                  </div>

                  {/* Expanded */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                      <p className="text-sm text-gray-700 leading-relaxed mb-4 whitespace-pre-wrap">{c.message}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {status !== 'contacted' && (
                          <button
                            onClick={() => updateStatus(c._id, 'contacted')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0B8A8F] text-white text-xs font-bold rounded-lg hover:bg-[#097a7f] transition"
                          >
                            <CheckCheck size={13} /> Mark Contacted
                          </button>
                        )}
                        {status !== 'new' && (
                          <button
                            onClick={() => updateStatus(c._id, 'new')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg hover:bg-amber-600 transition"
                          >
                            Mark New
                          </button>
                        )}
                        {status !== 'archived' && (
                          <button
                            onClick={() => updateStatus(c._id, 'archived')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-100 transition"
                          >
                            <Archive size={13} /> Archive
                          </button>
                        )}
                        <a
                          href={`mailto:${c.email}?subject=Re: ${encodeURIComponent(c.serviceInterest || 'Your inquiry')}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#001a4d] text-white text-xs font-bold rounded-lg hover:bg-[#002a6d] transition"
                        >
                          <Mail size={13} /> Reply by Email
                        </a>
                        <button
                          onClick={() => deleteContact(c._id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 text-xs font-bold rounded-lg hover:bg-red-100 transition ml-auto"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContacts;
